import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ open, project, onClose }) {
  const [idx, setIdx] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) setIdx(0);
  }, [open, project?.id]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      document.body.style.overflow = "hidden";
    } else {
      const t = setTimeout(() => setMounted(false), 280);
      document.body.style.overflow = "";
      return () => clearTimeout(t);
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (e.key === "Escape") onClose();
      if (!project?.media || project.media.length < 2) return;
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % project.media.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + project.media.length) % project.media.length);
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [open, project, onClose]);

  if (!mounted && !open) return null;
  if (!project) return null;

  const media = project.media || [];
  const current = media[idx];
  const multi = media.length > 1;
  const goPrev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + media.length) % media.length); };
  const goNext = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % media.length); };

  return (
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${project.client} — ${project.category}`}
      onClick={onClose}
      className={`fixed inset-0 z-[90] flex items-center justify-center px-4 py-6 sm:py-10 transition-opacity duration-500 ease-out ${open ? "opacity-100" : "opacity-0"}`}
    >
      <div aria-hidden="true" className="absolute inset-0 bg-black/55 backdrop-blur-[6px]" />

      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-card"
        className={`relative z-[1] w-full max-w-[92vw] sm:max-w-[75vw] max-h-[90svh] rounded-[22px] overflow-hidden border border-white/15 bg-white/[0.05] backdrop-blur-2xl backdrop-saturate-150 shadow-[0_30px_120px_-20px_rgba(0,0,0,0.65)] transition-all duration-[520ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-[0.92] translate-y-6"}`}
      >
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-white/75">
            <span className={`h-1.5 w-1.5 rounded-full ${project.type === "video" ? "bg-[#E6FF55] animate-pulse" : "bg-white/70"}`} />
            <span>{project.type === "video" ? "Reel" : project.type === "text" ? "Brand" : "Case"}</span>
            {multi && (
              <span className="text-white/45">
                · {String(idx + 1).padStart(2, "0")} / {String(media.length).padStart(2, "0")}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Close"
            className="pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/15 hover:bg-[#E6FF55] hover:text-black hover:ring-[#E6FF55] transition-colors"
          >
            <X size={14} strokeWidth={2.2} />
          </button>
        </div>

        {/* Media */}
        <div className="relative w-full bg-black" style={{ maxHeight: "78svh" }}>
          {current?.type === "video" ? (
            <video
  key={`${current.src}-${idx}`}
  src={current.src}
  poster={current.poster}
  autoPlay
  muted
  loop
  playsInline
  controls
  className="w-full block"
  style={{ minHeight: "300px", maxHeight: "78svh", objectFit: "contain" }}
/>
          ) : current?.type === "image" ? (
            <img
              key={`${current.src}-${idx}`}
              src={current.src}
              alt={`${project.client} — ${idx + 1}`}
              className="w-full block"
              style={{ maxHeight: "78svh", objectFit: "contain" }}
            />
          ) : null}

          {multi && (
            <>
              <button type="button" onClick={goPrev} data-testid="lightbox-prev" aria-label="Previous" className="absolute left-2.5 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/15 backdrop-blur-md hover:bg-[#E6FF55] hover:text-black hover:ring-[#E6FF55] transition">
                <ChevronLeft size={16} strokeWidth={2} />
              </button>
              <button type="button" onClick={goNext} data-testid="lightbox-next" aria-label="Next" className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/15 backdrop-blur-md hover:bg-[#E6FF55] hover:text-black hover:ring-[#E6FF55] transition">
                <ChevronRight size={16} strokeWidth={2} />
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex items-end justify-between gap-3 bg-black/30">
          <div className="min-w-0">
            <div className="font-display text-[18px] tracking-[-0.015em] text-white truncate">{project.client}</div>
            <div className="mt-0.5 font-mono text-[10px] tracking-[0.22em] uppercase text-white/55 truncate">{project.category}</div>
          </div>
          {multi && (
            <div data-testid="lightbox-dots" className="flex items-center gap-1.5 shrink-0">
              {media.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-[#E6FF55]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
